import { useContext, useEffect, useMemo, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import STORE from 'store';

// Settings saved before presets had labels only carry presetAmounts; read them
// as unlabelled presets.
export const toAdditionalPaymentPresets = (
  settings: BonusOverrideSettings.AdditionalPaymentSettings | undefined,
): BonusOverrideSettings.AdditionalPaymentPreset[] => {
  if (!settings) {
    return [];
  }
  if (settings.presets) {
    return settings.presets;
  }
  return (settings.presetAmounts ?? []).map((amount) => ({
    amount,
    label: '',
  }));
};

// Listens to a bonus program's additional payment settings and returns the
// staff-configured quick-pick options (amount + label), one per amount and
// sorted by amount. None configured (the default) or a failed read = no
// presets, so only a custom amount can be entered.
const useAdditionalPaymentPresets = ({
  bonusProgramId,
}: {
  bonusProgramId: string;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [presets, setPresets] = useState<
    BonusOverrideSettings.AdditionalPaymentPreset[]
  >([]);

  useEffect(() => {
    let isActive = true;
    const unsubscribe = STORE.bonus
      .getBonusAdditionalPaymentSettingsRef({
        accountId: selectedAccountId,
        bonusProgramId,
      })
      .onSnapshot(
        (snapshot) => {
          if (!isActive) {
            return;
          }
          setPresets(toAdditionalPaymentPresets(snapshot.data()));
        },
        () => {
          if (!isActive) {
            return;
          }
          setPresets([]);
        },
      );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [bonusProgramId, selectedAccountId]);

  return useMemo(() => {
    const byAmount = new Map<
      number,
      BonusOverrideSettings.AdditionalPaymentPreset
    >();
    presets
      .filter((preset) => preset.amount > 0)
      .forEach((preset) => {
        if (!byAmount.has(preset.amount)) {
          byAmount.set(preset.amount, {
            amount: preset.amount,
            label: (preset.label ?? '').trim(),
          });
        }
      });
    return Array.from(byAmount.values()).sort((a, b) => a.amount - b.amount);
  }, [presets]);
};

export default useAdditionalPaymentPresets;
